import 'normalize.css';
import React, { useState, useEffect } from 'react';
import { HashRouter, Routes, Route, useLocation } from 'react-router-dom';
import FileArgProvider from './components/FilesArgContext';
import TopSearch from './components/TopSearch';
import Universal from './components/Universal';
import ResultSearch from './components/ResultSearch';
import DownloadPage from './components/Download/download';
import { fetchIndustries, getSeriesCategories } from './services/services';
import { IntlProvider } from 'react-intl';
import { messages } from './locales';
import { lang } from './config/config';
import { FormattedMessage } from 'react-intl';
import './App.css';

// 常量定义
export const INDUSTRY_MAP = {
  EnterpriseNetwork: 0,
  IndustrialDigitalization: 1,
  DigitalEnergy: 2,
  SmartCommerce: 3,
  Mobility: 4,
  CloudServices: 5,
};

export const INDUSTRIES_WITH_HANDBOOK = [
  'EnterpriseNetwork',
  'IndustrialDigitalization',
  'SmartCommerce',
  'Mobility',
  'DigitalEnergy',
];

// 错误边界组件
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    console.error('Error occurred:', error);
  }

  render() {
    if (this.state.hasError) {
      return <h1>出错了。请稍后再试。</h1>;
    }
    return this.props.children;
  }
}

// 主要内容布局组件
const MainLayout = ({
  flag,
  searchArg,
  setFlag,
  setSearchArg,
  state,
  setState,
}) => {
  const location = useLocation();

  if (location.pathname.startsWith('/download')) {
    return null;
  }

  return (
    <>
      <TopSearch
        assignFlag={val => setFlag(val)}
        searchArg={searchArg}
        assignSearchArg={val => setSearchArg(val)}
      />
      {flag && searchArg.value ? (
        <ResultSearch
          searchArg={searchArg}
          assignSearchArg={val => setSearchArg(val)}
        />
      ) : (
        <Universal
          state={state}
          setState={setState}
        />
      )}
    </>
  );
};

function App() {
  const [flag, setFlag] = useState(false);
  const [searchArg, setSearchArg] = useState({
    value: '',
    type: 'firmware',
    identify: false,
  });

  const [state, setState] = useState({
    industries: [],
    typeSeries: [],
    selectedIndustry: localStorage.getItem('one') || 'EnterpriseNetwork',
    openType: null,
    selectedSeries: null,
    loading: true,
    keyword: '',
  });

  const { selectedIndustry } = state;

  // 加载行业列表
  useEffect(() => {
    const loadIndustries = async () => {
      try {
        const data = await fetchIndustries();
        const sortedIndustries = Array(Object.keys(INDUSTRY_MAP).length).fill(
          null
        );

        data.result.forEach(item => {
          const index = INDUSTRY_MAP[item.id];
          if (index !== undefined) {
            sortedIndustries[index] = item;
          }
        });

        const filteredIndustries = sortedIndustries.filter(Boolean);
        setState(prevState => ({
          ...prevState,
          industries: filteredIndustries,
        }));
      } catch (error) {
        console.error('Error loading industries:', error);
      }
    };

    loadIndustries();
  }, []);

  // 加载类型系列列表
  useEffect(() => {
    const loadTypeSeries = async () => {
      if (!selectedIndustry) return;

      try {
        setState(prev => ({ ...prev, loading: true }));

        const data = await getSeriesCategories(selectedIndustry);
        const filteredSeries = data.result.product_categories.filter(
          item => item.product_series && item.product_series.length > 0
        );

        let updatedSeries = filteredSeries;

        if (INDUSTRIES_WITH_HANDBOOK.includes(selectedIndustry)) {
          const handbookCategory = {
            id: `${selectedIndustry}_handbook`,
            name: <FormattedMessage id='handbook' />,
            product_series: [
              {
                id: `${selectedIndustry}_handbook`,
                name: <FormattedMessage id='handbook' />,
                entries: ['ProductBrochures'],
                desc: 'handbook',
                category_id: 'handbook',
              },
            ],
          };
          updatedSeries = [...filteredSeries, handbookCategory];
        }

        setState(prevState => ({
          ...prevState,
          typeSeries: updatedSeries,
          loading: false,
        }));
      } catch (error) {
        console.error('Error loading type series:', error);
        setState(prev => ({ ...prev, loading: false }));
      }
    };

    loadTypeSeries();
  }, [selectedIndustry]);

// 路由监听
useEffect(() => {
  const handleRouteChange = () => {
    const currentHashname = window.location.hash;
    const pathParts = currentHashname.split('/');
    const currentModel = pathParts[1] || 'EnterpriseNetwork';

    const array = Object.keys(INDUSTRY_MAP);
    const isValidIndustry = array.includes(pathParts[1]);

    // 如果是有效的行业路径
    if (isValidIndustry) {
      const typeId = pathParts[2];
      const seriesId = pathParts[3];

      // 检查是否是手册路由
      const isHandbookRoute = typeId?.toLowerCase().includes('handbook');

      // 验证路由的合法性
      const isValidRoute = isHandbookRoute ?
        (seriesId === 'handbook-list') : // 如果是手册路由，检查 seriesId 是否为 handbook-list
        true; // 如果不是手册路由，保持原有逻辑

      if (isValidRoute) {
        // 只在路由真正变化时更新状态
        if (
          localStorage.getItem('one') !== currentModel ||
          localStorage.getItem('two') !== typeId ||
          localStorage.getItem('three') !== seriesId
        ) {
          localStorage.setItem('one', currentModel);
          localStorage.setItem('two', typeId);
          localStorage.setItem('three', seriesId);

          setState(prevState => {
            // 只在行业确实改变时才更新状态
            if (prevState.selectedIndustry !== currentModel) {
              return {
                ...prevState,
                selectedIndustry: currentModel,
                loading: true,
              };
            }
            return prevState;
          });
        }
      } else {
        // 无效的手册路由，重定向到默认路由
        const defaultIndustry = localStorage.getItem('one') || 'EnterpriseNetwork';
        window.location.replace(`#/${defaultIndustry}/CellularRouter300/IR302`);
      }
    }
    // 处理无效路径
    else if (pathParts.length > 1 && !pathParts[1].startsWith('download')) {
      const defaultIndustry = localStorage.getItem('one') || 'EnterpriseNetwork';
      const pathname = `#/${defaultIndustry}/CellularRouter300/IR302`;

      if (window.location.hash !== pathname) {
        window.location.replace(pathname);
      }
    }
  };

  // 初始化时执行一次
  handleRouteChange();

  // 添加路由监听
  window.addEventListener('hashchange', handleRouteChange);

  return () => {
    window.removeEventListener('hashchange', handleRouteChange);
  };
}, []); // 移除依赖项，避免循环更新

  // 修改加载类型系列列表的逻辑
  useEffect(() => {
    const loadTypeSeries = async () => {
      if (!selectedIndustry) return;

      try {
        const data = await getSeriesCategories(selectedIndustry);
        const filteredSeries = data.result.product_categories.filter(
          item => item.product_series && item.product_series.length > 0
        );

        let updatedSeries = filteredSeries;

        if (INDUSTRIES_WITH_HANDBOOK.includes(selectedIndustry)) {
          const handbookCategory = {
            id: `${selectedIndustry}_handbook`,
            name: <FormattedMessage id='handbook' />,
            product_series: [
              {
                id: `${selectedIndustry}_handbook`,
                name: <FormattedMessage id='handbook' />,
                entries: ['ProductBrochures'],
                desc: 'handbook',
                category_id: 'handbook',
              },
            ],
          };
          updatedSeries = [...filteredSeries, handbookCategory];
        }

        setState(prevState => ({
          ...prevState,
          typeSeries: updatedSeries,
          loading: false,
        }));
      } catch (error) {
        console.error('Error loading type series:', error);
        setState(prev => ({ ...prev, loading: false }));
      }
    };

    // 重置 typeSeries 并加载新数据
    setState(prev => ({ ...prev, typeSeries: [], loading: true }));
    loadTypeSeries();
  }, [selectedIndustry]);

  return (
    <IntlProvider
      locale={lang}
      messages={messages[lang]}
    >
      <FileArgProvider>
        <HashRouter>
          <ErrorBoundary>
            <Routes>
              <Route
                path='/download/:docInfo'
                element={<DownloadPage />}
              />
              <Route
                path='/*'
                element={
                  <MainLayout
                    flag={flag}
                    searchArg={searchArg}
                    setFlag={setFlag}
                    setSearchArg={setSearchArg}
                    state={state}
                    setState={setState}
                  />
                }
              />
            </Routes>
          </ErrorBoundary>
        </HashRouter>
      </FileArgProvider>
    </IntlProvider>
  );
}

export default App;
